import { useState, useEffect } from 'react'
import { whatsAppNumbersAPI, metaAssetsAPI } from '../services/api'
import { Button, Card, Alert, Spinner, Select, Modal } from '../components/ui'
import { Formik, Form } from 'formik'
import { FormikInput, FormikSelect } from '../components/ui'
import * as Yup from 'yup'

const validationSchema = Yup.object().shape({
  whatsapp_number: Yup.string()
    .required('WhatsApp number is required')
    .max(20, 'WhatsApp number must be at most 20 characters'),
  display_name: Yup.string()
    .required('Display name is required')
    .max(255, 'Display name must be at most 255 characters'),
  phone_number_id: Yup.string().nullable().max(255, 'Phone number ID must be at most 255 characters'),
  whatsapp_business_account_id: Yup.string().nullable().max(255, 'WhatsApp Business Account ID must be at most 255 characters'),
  asset_id: Yup.number().required('Asset is required'),
})

const WhatsAppNumbersPage = () => {
  const [numbers, setNumbers] = useState([])
  const [assets, setAssets] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingAssets, setLoadingAssets] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selectedAssetId, setSelectedAssetId] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [editingNumber, setEditingNumber] = useState(null)
  const [deletingId, setDeletingId] = useState(null)

  // Load assets on mount
  useEffect(() => {
    loadAssets()
  }, [])

  // Load numbers when asset filter changes
  useEffect(() => {
    if (selectedAssetId) {
      loadNumbers()
    } else {
      setNumbers([])
    }
  }, [selectedAssetId])

  const loadAssets = async () => {
    try {
      setLoadingAssets(true)
      setError(null)
      const response = await metaAssetsAPI.getAssets({ asset_type: 'whatsapp_account' })

      if (response.data.success) {
        const assetList = response.data.data?.data || response.data.data || []
        setAssets(assetList)
        
        // Auto-select first asset if available
        if (assetList.length > 0 && !selectedAssetId) {
          setSelectedAssetId(assetList[0].id.toString())
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load assets')
    } finally {
      setLoadingAssets(false)
    }
  }

  const loadNumbers = async () => {
    if (!selectedAssetId) return

    try {
      setLoading(true)
      setError(null)
      const response = await whatsAppNumbersAPI.getAll({ asset_id: selectedAssetId })

      if (response.data.success) {
        const numberList = Array.isArray(response.data.data) 
          ? response.data.data 
          : response.data.data?.data || []
        setNumbers(numberList)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load WhatsApp numbers')
      setNumbers([])
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setEditingNumber(null)
    setShowModal(true)
  }

  const handleEdit = (number) => {
    setEditingNumber(number)
    setShowModal(true)
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this WhatsApp number?')) {
      return
    }

    try {
      setDeletingId(id)
      setError(null)
      const response = await whatsAppNumbersAPI.delete(id)

      if (response.data.success) {
        setSuccess('WhatsApp number deleted successfully')
        await loadNumbers()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete WhatsApp number')
      setTimeout(() => setError(null), 5000)
    } finally {
      setDeletingId(null)
    }
  }

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null)
      setSuccess(null)

      if (editingNumber) {
        // Update
        const response = await whatsAppNumbersAPI.update(editingNumber.id, values)
        if (response.data.success) {
          setSuccess('WhatsApp number updated successfully')
          setShowModal(false)
          resetForm()
          await loadNumbers()
          setTimeout(() => setSuccess(null), 3000)
        }
      } else {
        // Create
        const response = await whatsAppNumbersAPI.create(values)
        if (response.data.success) {
          setSuccess('WhatsApp number created successfully')
          setShowModal(false)
          resetForm()
          await loadNumbers()
          setTimeout(() => setSuccess(null), 3000)
        }
      }
    } catch (err) {
      setError(err.response?.data?.message || `Failed to ${editingNumber ? 'update' : 'create'} WhatsApp number`)
      setTimeout(() => setError(null), 5000)
    } finally {
      setSubmitting(false)
    }
  }

  const assetOptions = assets.map(asset => ({
    value: asset.id.toString(),
    label: `${asset.asset_name} (${asset.asset_type})`
  }))

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">WhatsApp Numbers</h1>
        <p className="text-gray-600 mt-1">
          Manage your WhatsApp phone numbers.
        </p>
      </div>

      {error && (
        <div className="mb-4">
          <Alert variant="danger" onClose={() => setError(null)}>
            {error}
          </Alert>
        </div>
      )}

      {success && (
        <div className="mb-4">
          <Alert variant="success" onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        </div>
      )}

      <Card>
        <div className="p-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-6 space-y-4 md:space-y-0">
            <div className="md:w-1/3">
              <Select
                id="asset-filter"
                label="Filter by Asset"
                options={assetOptions}
                placeholder={loadingAssets ? "Loading assets..." : "Select an asset"}
                value={selectedAssetId}
                onChange={(e) => setSelectedAssetId(e.target.value)}
                disabled={loadingAssets || assets.length === 0}
              />
            </div>
            <Button
              variant="primary"
              onClick={handleCreate}
              disabled={!selectedAssetId}
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add WhatsApp Number
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Spinner size="lg" />
            </div>
          ) : !selectedAssetId ? (
            <div className="text-center py-12 text-gray-500">
              <p>Please select an asset to view WhatsApp numbers</p>
            </div>
          ) : numbers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <p className="text-lg font-medium mb-2">No WhatsApp numbers found</p>
              <p className="text-sm mb-4">Get started by adding your first WhatsApp number</p>
              <Button variant="primary" onClick={handleCreate}>
                Add WhatsApp Number
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      WhatsApp Number
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Display Name
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone Number ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Business Account ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {numbers.map((number) => (
                    <tr key={number.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {number.whatsapp_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {number.display_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {number.phone_number_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {number.whatsapp_business_account_id || '-'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleEdit(number)}
                            className="text-blue-600 hover:text-blue-900"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(number.id)}
                            disabled={deletingId === number.id}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50"
                          >
                            {deletingId === number.id ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setEditingNumber(null)
        }}
        title={editingNumber ? 'Edit WhatsApp Number' : 'Add WhatsApp Number'}
        size="lg"
      >
        <Formik
          initialValues={{
            whatsapp_number: editingNumber?.whatsapp_number || '',
            display_name: editingNumber?.display_name || '',
            phone_number_id: editingNumber?.phone_number_id || '',
            whatsapp_business_account_id: editingNumber?.whatsapp_business_account_id || '',
            asset_id: editingNumber?.asset_id || parseInt(selectedAssetId) || '',
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ isSubmitting }) => (
            <Form className="space-y-4">
              <FormikSelect
                id="asset_id"
                name="asset_id"
                label="Asset"
                options={assetOptions}
                placeholder="Select an asset"
                required
                disabled={!!editingNumber}
              />

              <FormikInput
                id="whatsapp_number"
                name="whatsapp_number"
                label="WhatsApp Number"
                placeholder="Enter WhatsApp number (e.g., 7828796979)"
                required
              />

              <FormikInput
                id="display_name"
                name="display_name"
                label="Display Name"
                placeholder="Enter display name"
                required
              />

              <FormikInput
                id="phone_number_id"
                name="phone_number_id"
                label="Phone Number ID"
                placeholder="Enter phone number ID (optional)"
                helperText="This will be automatically set when creating on Meta"
              />

              <FormikInput
                id="whatsapp_business_account_id"
                name="whatsapp_business_account_id"
                label="WhatsApp Business Account ID"
                placeholder="Enter WhatsApp Business Account ID (optional)"
                helperText="This will be automatically set when creating on Meta"
              />

              <div className="flex justify-end space-x-3 pt-4">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setShowModal(false)
                    setEditingNumber(null)
                  }}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  loading={isSubmitting}
                  disabled={isSubmitting}
                >
                  {editingNumber ? 'Update' : 'Create'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default WhatsAppNumbersPage

